/**
 * Unified compatibility UX constants — Wave 6C
 *
 * Single source of truth for all compatibility copy strings and badge variants.
 * Used by CompatibilityCallout, WidgetPickerPanel, WidgetFrame, WidgetConfigSheet.
 */

/** 4-state compatibility taxonomy */
export type CompatibilityState = 'compatible' | 'partial' | 'incompatible' | 'unavailable';

export type CompatibilityReasonSeverity = 'error' | 'warn' | 'info';

export interface CompatibilityReasonLike {
  code?: string;
  message?: string;
  severity?: string;
}

/** Compatibility error codes from backend COMPAT_* constants */
export type CompatibilityErrorCode =
  | 'COMPAT_SIZE_TOO_SMALL'
  | 'SIZE_CONSTRAINT'
  | 'COMPAT_DESCRIPTOR_MISMATCH'
  | 'COMPAT_VISUAL_TYPE_UNSUPPORTED'
  | 'DESCRIPTOR_SPOOF_MISMATCH'
  | string; // future codes

export interface CompatibilityBadgeCopy {
  badgeText: string;
  tooltipText: string;
  actions?: {
    fix?: string;
    tryAnyway?: string;
    retry?: string;
  };
  /** ARIA label for badge element */
  ariaLabel: string;
}

export const COMPATIBILITY_COPY: Record<CompatibilityState, CompatibilityBadgeCopy> & {
  incompatibleByCode: Partial<Record<string, CompatibilityBadgeCopy>>;
} = {
  compatible: {
    badgeText: '',
    tooltipText: '',
    ariaLabel: 'Compatible',
  },
  partial: {
    badgeText: 'Needs review',
    tooltipText: 'Some data may be missing for this period. You can still use this widget after review.',
    actions: {
      tryAnyway: 'Try anyway',
      retry: 'Retry',
    },
    ariaLabel: 'Needs review — some data may be missing',
  },
  incompatible: {
    badgeText: 'Blocked',
    tooltipText: 'This widget cannot show data with the current settings.',
    actions: {
      fix: 'Fix',
      retry: 'Retry',
    },
    ariaLabel: 'Blocked — review settings',
  },
  unavailable: {
    badgeText: 'Check skipped',
    tooltipText: 'Compatibility check failed. You can still add this widget.',
    actions: {
      retry: 'Retry',
    },
    ariaLabel: 'Compatibility check skipped',
  },
  incompatibleByCode: {
    COMPAT_SIZE_TOO_SMALL: {
      badgeText: 'Needs space',
      tooltipText: 'Widget requires more grid cells. You can resize it after adding.',
      actions: {
        fix: 'Resize to fit',
        retry: 'Retry',
      },
      ariaLabel: 'Incompatible — widget needs more space',
    },
    SIZE_CONSTRAINT: {
      badgeText: 'Needs space',
      tooltipText: 'Widget requires more grid cells to display correctly. Resize it after adding.',
      actions: {
        fix: 'Resize to fit',
        retry: 'Retry',
      },
      ariaLabel: 'Incompatible — widget needs more grid space',
    },
    COMPAT_DESCRIPTOR_MISMATCH: {
      badgeText: 'Blocked',
      tooltipText: "This metric isn't available for this widget. Choose another metric or replace the widget.",
      actions: {
        fix: 'Reconfigure',
        retry: 'Retry',
      },
      ariaLabel: 'Blocked — metric unavailable',
    },
    COMPAT_VISUAL_TYPE_UNSUPPORTED: {
      badgeText: 'Not available',
      tooltipText: "This widget type isn't supported in your current setup.",
      actions: {
        fix: 'Replace widget',
        retry: 'Retry',
      },
      ariaLabel: 'Incompatible — widget type not available',
    },
    DESCRIPTOR_SPOOF_MISMATCH: {
      badgeText: 'Needs review',
      tooltipText: 'This widget setup changed after it was saved. Re-open configuration to refresh it.',
      actions: {
        fix: 'Reconfigure',
        retry: 'Retry',
      },
      ariaLabel: 'Needs review — widget setup changed',
    },
  },
};

export const COMPATIBILITY_SEVERITY_TO_STATE: Record<CompatibilityReasonSeverity, CompatibilityState> = {
  error: 'incompatible',
  warn: 'partial',
  info: 'partial',
};

export function normaliseCompatibilitySeverity(value: unknown): CompatibilityReasonSeverity | undefined {
  if (typeof value !== 'string') return undefined;
  const lowered = value.toLowerCase();
  if (lowered === 'error' || lowered === 'warn' || lowered === 'info') {
    return lowered;
  }
  return undefined;
}

export function deriveCompatibilitySeverity(
  reasons: Array<CompatibilityReasonLike> | undefined,
  severity: unknown,
  warnings: Array<unknown> | undefined,
): CompatibilityReasonSeverity | undefined {
  const topLevel = normaliseCompatibilitySeverity(severity);
  if (topLevel) return topLevel;

  const reasonSeverities = (reasons ?? [])
    .map((reason) => normaliseCompatibilitySeverity(reason?.severity))
    .filter((s): s is CompatibilityReasonSeverity => Boolean(s));

  if (reasonSeverities.includes('error')) return 'error';
  if (reasonSeverities.includes('warn')) return 'warn';
  if (reasonSeverities.includes('info')) return 'info';
  if ((warnings ?? []).length > 0) return 'warn';
  return undefined;
}

/** Derive CompatibilityState from backend compatibility check result */
export function deriveCompatibilityState(
  compatible: boolean | undefined,
  warnings: Array<unknown> | undefined,
  reasons?: Array<CompatibilityReasonLike>,
  severity?: unknown,
  _errorCode?: string,
): CompatibilityState {
  if (compatible === undefined) return 'unavailable';
  if (!compatible) return 'incompatible';

  const resolvedSeverity = deriveCompatibilitySeverity(reasons, severity, warnings);
  if (resolvedSeverity) {
    return COMPATIBILITY_SEVERITY_TO_STATE[resolvedSeverity];
  }

  if ((warnings ?? []).length > 0) return 'partial';
  return 'compatible';
}

/** Get the badge copy for a state + optional error code */
export function getCompatibilityCopy(
  state: CompatibilityState,
  errorCode?: string,
): CompatibilityBadgeCopy {
  if (state === 'incompatible' && errorCode && COMPATIBILITY_COPY.incompatibleByCode[errorCode]) {
    return COMPATIBILITY_COPY.incompatibleByCode[errorCode]!;
  }
  return COMPATIBILITY_COPY[state];
}
